// SkillAnalyzer.js — Root orchestrator
import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../../shared/SEO';
import SkillAnalyzerHeader from './SkillAnalyzerHeader';
import SkillAnalyzerSearch from './Search/SkillAnalyzerSearch';
import SkillAnalyzerPool from './Selection/SkillAnalyzerPool';
import SkillAnalyzerResults from './Results/SkillAnalyzerResults';
import useSkillDetail from './Search/useSkillDetail';
import { analyzeSkills } from './Engine/analysisEngine';

/**
 * SkillAnalyzer — Skill compatibility analysis tool.
 *
 * State:
 *   selectedSkills: [{ Id_skill, skillName, icon_id, skill_effects, chosenScenario }]
 *
 * Flow:
 *   1. User searches a skill, clicks → useSkillDetail fetches full data → added to pool
 *   2. User picks scenario via pills on each card
 *   3. useMemo re-runs analyzeSkills whenever selectedSkills changes
 *   4. Results displayed below
 */

const SkillAnalyzer = () => {
    const { t } = useTranslation();
    const { fetchSkill } = useSkillDetail();

    // Pool of selected (fully loaded) skills
    const [selectedSkills, setSelectedSkills] = useState([]);

    // Handler: user clicks a search result → fetch details → add to pool
    const handleSelect = useCallback(async (searchResult) => {
        // Avoid duplicates
        if (selectedSkills.some(s => s.Id_skill === searchResult.Id_skill)) return;

        const detail = await fetchSkill(searchResult.Id_skill);
        if (!detail) return;

        // Normalize data to expected shape
        const effects = detail.skill_effects || detail.effects || [];
        const firstScenario = effects[0]?.group_id ?? null;

        setSelectedSkills(prev => [...prev, {
            Id_skill: detail.Id_skill ?? detail.id_skill ?? searchResult.Id_skill,
            skillName: detail.Skill_name ?? detail.skill_name ?? searchResult.Skill_name,
            icon_id: detail.icon_id ?? searchResult.icon_id,
            color: detail.color ?? detail.Color ?? searchResult.color ?? null,
            skill_effects: effects,
            chosenScenario: firstScenario,
        }]);
    }, [selectedSkills, fetchSkill]);

    // Handler: remove skill from pool
    const handleRemove = useCallback((idSkill) => {
        setSelectedSkills(prev => prev.filter(s => s.Id_skill !== idSkill));
    }, []);

    // Handler: change scenario for a skill
    const handleScenarioChange = useCallback((idSkill, groupId) => {
        setSelectedSkills(prev => prev.map(s =>
            s.Id_skill === idSkill ? { ...s, chosenScenario: groupId } : s
        ));
    }, []);

    // Analysis: only runs when selectedSkills changes and we have ≥2 skills
    const analysisResult = useMemo(() => {
        if (selectedSkills.length < 2) return null;
        return analyzeSkills(selectedSkills);
    }, [selectedSkills]);

    const selectedIds = selectedSkills.map(s => s.Id_skill);

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white
            p-4 pt-32 md:p-8 md:pt-36">
            <SEO
                title={t('SkillAnalyzer.seo_title', 'Skill Analyzer — Compatibility Tool')}
                description={t('SkillAnalyzer.seo_desc', 'Analyze compatibility between N skills based on their conditions and preconditions.')}
            />
            <div className="max-w-7xl mx-auto space-y-8">

                {/* Header */}
                <SkillAnalyzerHeader />

                {/* Main layout: 2 columns on desktop */}
                <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6 items-start">

                    {/* ── Left column: Search + Pool ── */}
                    <div className="flex flex-col gap-4">

                        {/* Search panel */}
                        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-4">
                            {/* Panel header */}
                            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100 dark:border-slate-700">
                                <svg className="w-4 h-4 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                    Search Skills
                                </h3>
                            </div>
                            <SkillAnalyzerSearch
                                onSelect={handleSelect}
                                selectedIds={selectedIds}
                            />
                        </div>

                        {/* Pool panel */}
                        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-4">
                            <SkillAnalyzerPool
                                selectedSkills={selectedSkills}
                                onRemove={handleRemove}
                                onScenarioChange={handleScenarioChange}
                            />
                        </div>
                    </div>

                    {/* ── Right column: Results ── */}
                    <div className="min-w-0 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm p-6">
                        {/* Results header */}
                        <div className="flex items-center justify-between mb-6 pb-3 border-b border-slate-100 dark:border-slate-700">
                            <div className="flex items-center gap-2">
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white">
                                    <span className="text-violet-600 dark:text-violet-400">Analysis</span>
                                </h3>
                                {selectedSkills.length >= 2 && analysisResult && (
                                    <span className="text-xs px-2 py-0.5
                                        bg-violet-100 dark:bg-violet-900/30
                                        text-violet-600 dark:text-violet-300
                                        rounded-full border border-violet-200 dark:border-violet-800">
                                        {analysisResult.summary.totalAnalyzed} variables
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Empty state — only when nothing selected */}
                        {selectedSkills.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
                                <div className="w-16 h-16 rounded-2xl
                                    bg-violet-50 dark:bg-violet-900/20
                                    border border-violet-100 dark:border-violet-800/50
                                    flex items-center justify-center">
                                    <svg className="w-8 h-8 text-violet-300 dark:text-violet-600"
                                        fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-slate-500 dark:text-slate-400 font-medium">
                                        Add a skill to start
                                    </p>
                                    <p className="text-sm text-slate-400 dark:text-slate-600 mt-1">
                                        Effects and conditions appear with 1 skill,<br />compatibility analysis with 2 or more
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Results — visible from 1 skill */}
                        {selectedSkills.length >= 1 && (
                            <SkillAnalyzerResults
                                analysisResult={analysisResult}
                                skillCount={selectedSkills.length}
                                selectedSkills={selectedSkills}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SkillAnalyzer;
